import { useEffect, useRef } from 'react'
import { useMouseEvents } from '~/hooks/useMouseEvents'

/**
 * Calls `onTrigger` when a mousedown lands outside all of `elements`.
 *
 * Callers pass freshly-built arrays (`[ref.current, ...]`), so `elements` and
 * `onTrigger` are read through refs and deliberately kept out of the effect's
 * deps. Depending on them re-ran the effect on every render, and since
 * `onTrigger` typically closes a menu via setState, that produced an unbounded
 * render loop after a single outside click ("Maximum update depth exceeded").
 *
 * Side effect of dropping the deps: the element list is sampled once per
 * mousedown rather than re-evaluated when a later render populates the refs.
 * Benign for all callers — a not-yet-attached ref already read as "inside".
 */
export const useUserClickedExternalElements = (
  elements: (Element | null)[],
  scanChildren = true,
  onTrigger?: () => void,
) => {
  const mouseDownEvent = useMouseEvents()

  // Render-phase ref writes are deliberate: no StrictMode/transitions in this
  // app, so no discarded render can strand a stale value here. `scanChildren`
  // is a boolean (stable identity), so it stays in the deps rather than a ref.
  const elementsRef = useRef(elements)
  elementsRef.current = elements
  const onTriggerRef = useRef(onTrigger)
  onTriggerRef.current = onTrigger

  useEffect(() => {
    const currentElements = elementsRef.current
    if (currentElements.length === 0) return
    if (!(mouseDownEvent?.target instanceof Element)) return

    // Check if click is inside any popover content (Chakra v3 Portal)
    const isInsidePopover = (mouseDownEvent.target as Element).closest(
      '[data-scope="popover"]',
    )
    if (isInsidePopover) {
      return
    }

    if (
      currentElements.some(
        (element) =>
          // A null entry short-circuits to "inside" (null === null) so a
          // not-yet-attached ref never triggers; otherwise match the element
          // itself, or its subtree when scanChildren is set.
          element === (element && mouseDownEvent.target) ||
          (scanChildren && element!.contains(mouseDownEvent.target as Node)),
      )
    ) {
      return
    }

    onTriggerRef.current?.()
    // NOTE: elements/onTrigger are read through refs on purpose and kept out of
    // the deps — depending on them re-runs this effect every render (see
    // docblock). react-hooks/exhaustive-deps is disabled repo-wide today; when
    // it's re-enabled, suppress it *here* rather than adding elements/onTrigger
    // back, which would reintroduce the loop.
  }, [mouseDownEvent, scanChildren])
}
